import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { buildApiUrl } from '../config/apiConfig';
import { parseBookingDateTime } from '../utils/dateTimezone';
import {
  isNativeCacheEnabled,
  NativeCacheKeys,
  NativeCacheTtl,
  readNativeCache,
  writeNativeCache,
} from '../native/nativeDataCache';

export interface UpcomingBooking {
  bookingId: number;
  status: string;
  /** ISO avec timezone ou date + heure séparées */
  startDateTime?: string;
  endDateTime?: string;
  date: string;
  startTime: string;
  endTime: string;
  pro: { name: string; slug: string };
  service: { serviceName: string };
  animal: { name: string };
}

/** Statuts considérés comme "à venir" */
const UPCOMING_STATUSES = new Set(['validated', 'confirmed', 'pending', 'pending_client']);

function isUpcoming(booking: UpcomingBooking): boolean {
  if (!UPCOMING_STATUSES.has(booking.status)) return false;
  try {
    const { start } = parseBookingDateTime({
      startDateTime: booking.startDateTime,
      endDateTime: booking.endDateTime,
      date: booking.date,
      startTime: booking.startTime,
      endTime: booking.endTime,
    });
    return start > new Date();
  } catch {
    return false;
  }
}

interface UseUpcomingBookingsReturn {
  bookings: UpcomingBooking[];
  loading: boolean;
  refetch: () => void;
}

export function useUpcomingBookings(
  userId: number | undefined,
  limit = 3,
): UseUpcomingBookingsReturn {
  const [bookings, setBookings] = useState<UpcomingBooking[]>(() => {
    if (!userId || !isNativeCacheEnabled()) return [];
    return (
      readNativeCache<UpcomingBooking[]>(NativeCacheKeys.UPCOMING_BOOKINGS, userId, {
        ttlMs: NativeCacheTtl.BOOKINGS_MS,
      }) ?? []
    );
  });
  const [loading, setLoading] = useState(() => {
    if (!userId || !isNativeCacheEnabled()) return false;
    const cached = readNativeCache<UpcomingBooking[]>(
      NativeCacheKeys.UPCOMING_BOOKINGS,
      userId,
      { ttlMs: NativeCacheTtl.BOOKINGS_MS },
    );
    return !cached?.length;
  });
  const [version, setVersion] = useState(0);

  const refetch = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    if (!userId) return;

    let cancelled = false;
    setLoading(true);

    axios
      .get(buildApiUrl(`/get-bookings?user_id=${userId}`), {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      })
      .then((res) => {
        if (cancelled) return;
        const all: UpcomingBooking[] = res.data.bookings ?? [];
        const upcoming = all
          .filter(isUpcoming)
          .sort((a, b) => {
            const { start: da } = parseBookingDateTime({ startDateTime: a.startDateTime, endDateTime: a.endDateTime, date: a.date, startTime: a.startTime, endTime: a.endTime });
            const { start: db } = parseBookingDateTime({ startDateTime: b.startDateTime, endDateTime: b.endDateTime, date: b.date, startTime: b.startTime, endTime: b.endTime });
            return da.getTime() - db.getTime();
          })
          .slice(0, limit);
        setBookings(upcoming);
        if (isNativeCacheEnabled() && userId) {
          writeNativeCache(NativeCacheKeys.UPCOMING_BOOKINGS, userId, upcoming);
        }
      })
      .catch(() => setBookings([]))
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [userId, limit, version]);

  return { bookings, loading, refetch };
}
